import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { AccountMatch, SupportApi } from '../api/support'

/**
 * Support > Students (app:R22, R91): find a Student by part of their email, then act as them.
 *
 * Acting opens the App in a new tab, signed in as the Admin and answering as the Student, so
 * Support sees exactly what the Student sees and the Support controls beside it. The grant is
 * the API's; this screen only starts it. Every search, every grant and everything done while
 * acting is in the Audit Trail, which the Owner reads here under each Student.
 */
export function Support({ support, exam }: { support: SupportApi; exam: string }) {
  const [typed, setTyped] = useState('')
  const [asked, setAsked] = useState('')
  const [trailOf, setTrailOf] = useState<AccountMatch | null>(null)

  const found = useQuery({
    queryKey: ['support-find', asked],
    queryFn: () => support.find(asked),
    enabled: asked.length >= 3,
  })

  const act = useMutation({
    mutationFn: async (student: string) => {
      // Opened before the grant lands, in the click itself: a tab opened after an await is a
      // popup to the browser, and the browser blocks it.
      const tab = window.open('about:blank', '_blank')
      try {
        await support.actAs(student)
      } catch (failure) {
        tab?.close()
        throw failure
      }
      const to = `${userSiteOf(window.location.origin)}/support?exam=${encodeURIComponent(exam)}`
      if (tab) tab.location.href = to
      else window.location.assign(to)
    },
  })

  return (
    <>
      <h2 className="font-heading text-h2 font-bold text-heading">Students</h2>

      <form
        className="mt-4 flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setAsked(typed.trim())
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-small font-bold text-heading">Email contains</span>
          <input
            className={input}
            value={typed}
            minLength={3}
            onChange={(event) => setTyped(event.target.value)}
            aria-describedby="support-find-hint"
          />
        </label>
        <button type="submit" className={loud} disabled={typed.trim().length < 3}>
          Find
        </button>
        <p id="support-find-hint" className="w-full text-small text-muted">
          At least three letters. Emails are shown masked.
        </p>
      </form>

      {(found.error ?? act.error) && (
        <p role="alert" className="mt-4 border-l-2 border-red-700 pl-3 text-red-700">
          {(found.error ?? act.error)?.message}
        </p>
      )}

      {found.data && (
        <table className="mt-6 w-full text-left" aria-label="Students found">
          <thead className="border-b border-ink-100 text-small text-muted">
            <tr>
              <th className="py-2 pr-4">First name</th>
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Joined</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {found.data.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-muted">
                  No Student's email holds that.
                </td>
              </tr>
            )}
            {found.data.map((match) => (
              <tr key={match.student} className="border-b border-ink-100">
                <td className="py-2 pr-4 font-bold text-heading">{match.firstName}</td>
                <td className="py-2 pr-4">{match.email}</td>
                <td className="py-2 pr-4">{day.format(new Date(match.joinedAt))}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  <button type="button" className={quiet} onClick={() => setTrailOf(match)}>
                    Audit Trail
                  </button>{' '}
                  <button
                    type="button"
                    className={loud}
                    disabled={act.isPending}
                    onClick={() => act.mutate(match.student)}
                  >
                    Act As {match.firstName}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {trailOf && <Trail support={support} student={trailOf} />}
    </>
  )
}

function Trail({ support, student }: { support: SupportApi; student: AccountMatch }) {
  const trail = useQuery({
    queryKey: ['support-trail', student.student],
    queryFn: () => support.trail(student.student),
  })

  return (
    <section className="mt-8" aria-label={`Audit Trail for ${student.firstName}`}>
      <div className="flex items-center gap-4">
        <h3 className="font-heading text-h3 font-bold text-heading">Audit Trail · {student.firstName}</h3>
        <button type="button" className={`${quiet} ml-auto`} onClick={() => void trail.refetch()}>
          Refresh
        </button>
      </div>
      {trail.isError ? (
        <p role="alert" className="mt-3 border-l-2 border-red-700 pl-3 text-red-700">
          {trail.error.message}
        </p>
      ) : trail.isPending ? (
        <p className="mt-3">One moment...</p>
      ) : trail.data.length === 0 ? (
        <p className="mt-3 text-muted">Nothing has been done as this Student.</p>
      ) : (
        <table className="mt-3 w-full text-left text-small" aria-label="Audit Trail">
          <thead className="border-b border-ink-100 text-muted">
            <tr>
              <th className="py-2 pr-4">When</th>
              <th className="py-2 pr-4">Action</th>
              <th className="py-2 pr-4">Outcome</th>
              <th className="py-2">Admin</th>
            </tr>
          </thead>
          <tbody>
            {trail.data.map((line) => (
              <tr key={`${line.at}-${line.action}`} className="border-b border-ink-100">
                <td className="py-2 pr-4 whitespace-nowrap">{moment.format(new Date(line.at))}</td>
                <td className="py-2 pr-4 font-mono">{line.action}</td>
                <td className="py-2 pr-4">{line.detail}</td>
                <td className="py-2 font-mono text-muted">{line.admin}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

/**
 * Where the App is, read off where the Admin Site is: `admin-app.` before the platform's name
 * deployed, and the port beside this one locally.
 */
// ponytail: derived rather than configured; a config key when an Environment breaks the pattern.
export function userSiteOf(origin: string): string {
  const url = new URL(origin)
  if (url.hostname === 'localhost') return `${url.protocol}//localhost:5173`
  return `${url.protocol}//${url.host.replace(/^admin-app\./, 'app.')}`
}

const day = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeZone: 'Europe/London' })
const moment = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'medium', timeZone: 'Europe/London' })

const input = 'w-72 rounded-md border border-ink-100 bg-card px-3 py-2'

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-3 py-1.5 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-md bg-green-800 px-3 py-1.5 font-bold text-white hover:bg-green-900 disabled:opacity-55'
